import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { ArrowLeft, Search, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [searchQuery]);

  useEffect(() => {
    setLoading(true);
    fetchProducts(debouncedSearch);
  }, [categoryId, debouncedSearch]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/categories`).then(res => {
      setCategory(res.data.find(c => c.id === categoryId));
    }).catch(() => {});
  }, [categoryId]);

  const fetchProducts = async (search) => {
    try {
      const params = new URLSearchParams({ page: 1, limit: 500 });
      if (search) params.set('search', search);
      const res = await axios.get(`${BACKEND_URL}/api/products/category/${categoryId}/paginated?${params}`);
      setProducts(res.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#1A3626] hover:text-[#C05A3E] mb-4 transition-colors" data-testid="back-button">
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Back to Categories</span>
        </button>

        {category && (
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A3626]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="category-title">
              {category.name}
            </h1>
            <span className="text-sm text-gray-500" data-testid="product-count">
              {products.length} product{products.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        <div className="max-w-xl mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#1A3626] focus:ring-0 text-base" data-testid="product-search" />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-products">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#1A3626] mb-2" />
            <p className="text-gray-500">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12" data-testid="no-products">
            <p className="text-gray-500 text-lg">
              {searchQuery ? `No products found matching "${searchQuery}"` : 'No products available in this category yet.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4" data-testid="products-grid">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
